<template>
  <section id="about" class="profile">
    <div class="container">
      <header class="profile-head">
        <span ref="ghostEl" class="ghost profile-ghost" aria-hidden="true">{{
          numeral
        }}</span>
        <span class="label">{{ $t("profile.kicker") }}</span>
        <h2 class="h-section profile-title">{{ $t("profile.title") }}</h2>
      </header>

      <div class="profile-grid">
        <div class="profile-prose">
          <p class="profile-p" v-html="$t('about.paragraph1')"></p>
          <p class="profile-p" v-html="$t('about.paragraph2')"></p>
          <p class="profile-p" v-html="$t('about.paragraph3')"></p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from "vue";
import {
  registerLayer,
  DEPTH,
} from "@/composables/useDepth";

export default {
  name: "ProfileSection",
  props: {
    numeral: { type: String, default: "05" },
  },
  setup() {
    const ghostEl = ref(null);
    let stopDepth = null;

    onMounted(() => {
      stopDepth = registerLayer(ghostEl.value, { k: DEPTH.GHOST });
    });

    onBeforeUnmount(() => {
      if (stopDepth) stopDepth();
    });

    return { ghostEl };
  },
};
</script>

<style scoped>
.profile {
  /* No top padding: §3.4 makes this section's first rule the ledger's last
     row's bottom rule, so nothing may sit between them. */
  padding: 0 0 var(--s-9);
}

/* The ledger's last row has no bottom rule; this is that rule. One
   continuous hairline across the seam between the two sections — drawn
   inside the container's padding box so it lands on exactly the same two
   x-positions as the row rules above it. A border on .container itself
   would run --edge wider on each side and the seam would visibly step. */
.profile .container::before {
  content: "";
  display: block;
  height: var(--hair-w);
  background: var(--hairline);
}

.profile-head {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--s-3);
  margin-top: var(--s-10);
  margin-bottom: var(--s-8);
}
.profile-ghost {
  --travel-y: calc(
    (var(--p, 0.5) - 0.5) * (1 - var(--k, 1)) * var(--travel) *
      var(--depth-scale)
  );
  transform: translate3d(var(--mx, 0px), var(--travel-y), 0) scale(var(--s, 1));
}
.profile-title {
  max-width: 18ch;
}

.profile-grid {
  display: grid;
  grid-template-columns: 1fr 380px;
  column-gap: 72px;
  align-items: start;
}

.profile-prose {
  display: flex;
  flex-direction: column;
  gap: var(--s-5);
  max-width: 58ch;
}
.profile-p {
  font-size: var(--t-body);
  line-height: 1.62;
  color: var(--text);
  text-wrap: pretty;
}
/* Emphasis is carried by weight and ink, never by a second colour — the
   only tinted things on this page are projects, and this is not one. */
.profile-p :deep(strong) {
  font-weight: 600;
  color: var(--ink);
}
.profile-p :deep(em) {
  font-style: italic;
  color: var(--ink);
}


@media (max-width: 900px) {
  .profile-grid {
    grid-template-columns: 1fr;
    row-gap: var(--s-8);
  }
  .spec {
    margin-top: 0;
  }
}
@media (max-width: 720px) {
  .profile {
    padding: 0 0 var(--s-8);
  }
  .profile-head {
    margin-top: var(--s-9);
    margin-bottom: var(--s-7);
  }
}

/* Everything renders at its final state; nothing waits on an observer. */
@media (prefers-reduced-motion: reduce) {
  .spec-row::before {
    transform: scaleX(1);
  }
  .spec-value {
    opacity: 1;
  }
}
</style>
